import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import useMeContext from '~/modules/me/useMeContext';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { dateToMidnightUTCString } from '~/modules/common/dates/convert';
import { useProjectResourcePlanPermissions } from '~/modules/resourcing/hooks';
import { ResourcingCurrentDateLine } from '~/modules/resourcing/common/components';
import {
  itemWidth,
  actionsWidth
} from '~/modules/common/charts/timeline/calculations';
import { useResourceRequestToolbarContext } from '../hooks';
import {
  ResourceRequestChartHeader,
  ResourceRequestChartFooter,
  ResourceAllocationChart,
  ResourceRequestChartContent
} from './components';
import { useUpdateProjectDateRangeHandlers } from './hooks';
import {
  useCompletedProjectResourceAllocations,
  useResourceAllocationChartEventHandlers
} from './components/ResourceAllocationChart/hooks';

const LEFT_WIDTH = 464;
const FOOTER_HEIGHT = 40;

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative'
  }
}));

export const ResourceRequestChart = ({
  resourceRequests,
  chartDates,
  scale,
  isEditSwitchEnabled,
  displayUnit,
  sort,
  onSortChange,
  chartDisplayDateRange,
  handleAddResourceRequestClick,
  loadingRows: loadingRequestRows,
  hasMoreRows: hasMoreRequestRows,
  loadMoreRows: loadMoreRequestRows,
  loadingMoreRows: loadingMoreRequestRows
}) => {
  const classes = useStyles();
  const { project } = useProjectContext();
  const {
    permissionsMap,
    featureFlags: { isPSARMPResourceActualEnabled }
  } = useMeContext();
  const {
    filter,
    isResourceActualModeEnabled
  } = useResourceRequestToolbarContext();

  const {
    updateProjectStartDate,
    updateProjectEndDate
  } = useUpdateProjectDateRangeHandlers(project);

  const {
    isViewAdvancedSettingsEnabled,
    isViewAvailabilitySettingsEnabled
  } = useProjectResourcePlanPermissions(permissionsMap);

  const formattedDateRange = useMemo(
    () =>
      chartDisplayDateRange
        ? {
            startDate: dateToMidnightUTCString(chartDisplayDateRange.startDate),
            endDate: dateToMidnightUTCString(chartDisplayDateRange.endDate)
          }
        : null,
    [chartDisplayDateRange]
  );

  const {
    handleAddAllocation,
    createAllocationLoading,
    updateAllocationLoading,
    smartFitResourceAllocation,
    openSmartFitDialogOrLoadingState,
    openShowSmartFitDialogOrLoading,
    closeShowSmartFitDialogOrLoading,
    setSmartFitResourceAllocation,
    onSmartFitCancel,
    handleAddResourceAllocation,
    handleResourceChangeAndUpdateResourceAllocation,
    handleEditResourceAllocation,
    handleAllocatedRoleChange,
    handleAllocatedUserChange,
    handleNewMergeResourceAllocation
  } = useResourceAllocationChartEventHandlers({
    project,
    sort,
    chartDateRange: formattedDateRange,
    showTimeOff: isViewAvailabilitySettingsEnabled,
    showHolidays: isViewAdvancedSettingsEnabled
  });

  const leftComponentWidth = actionsWidth + itemWidth * 2;

  const hasResourceRequests = resourceRequests.length > 0;

  const {
    resourceAllocations: completedResourceAllocations,
    loadingMore,
    loadingRows,
    loadMoreRows,
    hasMoreRows
  } = useCompletedProjectResourceAllocations({
    ...filter,
    projectUri: project.id,
    sort,
    dateRange: chartDisplayDateRange,
    isResourceActualModeEnabled
  });

  return (
    <div className={classes.container}>
      {isPSARMPResourceActualEnabled && (
        <ResourcingCurrentDateLine
          scale={scale}
          chartDates={chartDates}
          leftWidth={LEFT_WIDTH}
          footerHeight={FOOTER_HEIGHT}
        />
      )}
      <ResourceRequestChartHeader
        chartDates={chartDates}
        scale={scale}
        leftComponentWidth={leftComponentWidth}
        isEditSwitchEnabled={isEditSwitchEnabled}
        displayUnit={displayUnit}
        isSortEnabled
        showUserAssignmentColumn
        sort={sort}
        onSortChange={onSortChange}
      />
      <ResourceRequestChartContent
        chartDates={chartDates}
        project={project}
        hasResourceRequests={hasResourceRequests}
        resourceRequests={resourceRequests}
        scale={scale}
        isEditSwitchEnabled={isEditSwitchEnabled}
        leftComponentWidth={leftComponentWidth}
        showUserAssignmentColumn
        chartDisplayDateRange={chartDisplayDateRange}
        displayUnit={displayUnit}
        updateProjectStartDate={updateProjectStartDate}
        updateProjectEndDate={updateProjectEndDate}
        hasMoreRows={hasMoreRequestRows}
        loadingRows={loadingRequestRows}
        loadMoreRows={loadMoreRequestRows}
        loadingMoreRows={loadingMoreRequestRows}
      />
      <ResourceAllocationChart
        project={project}
        resourceAllocations={completedResourceAllocations}
        scale={scale}
        openSmartFitDialogOrLoadingState={openSmartFitDialogOrLoadingState}
        closeShowSmartFitDialogOrLoading={closeShowSmartFitDialogOrLoading}
        onSmartFitCancel={onSmartFitCancel}
        chartDates={chartDates}
        isEditSwitchEnabled={isEditSwitchEnabled}
        handleAddResourceAllocation={handleAddResourceAllocation}
        smartFitResourceAllocation={smartFitResourceAllocation}
        openShowSmartFitDialogOrLoading={openShowSmartFitDialogOrLoading}
        setSmartFitResourceAllocation={setSmartFitResourceAllocation}
        handleNewMergeResourceAllocation={handleNewMergeResourceAllocation}
        loadingRows={loadingRows}
        hasMoreRows={hasMoreRows}
        loadMoreRows={loadMoreRows}
        handleResourceChangeAndUpdateResourceAllocation={
          handleResourceChangeAndUpdateResourceAllocation
        }
        handleEditResourceAllocation={handleEditResourceAllocation}
        handleAllocatedRoleChange={handleAllocatedRoleChange}
        handleAllocatedUserChange={handleAllocatedUserChange}
        loadingMore={loadingMore}
        createAllocationLoading={createAllocationLoading}
        updateAllocationLoading={updateAllocationLoading}
      />

      <ResourceRequestChartFooter
        chartDates={chartDates}
        scale={scale}
        leftComponentWidth={leftComponentWidth}
        project={project}
        handleAddResourceRequestClick={handleAddResourceRequestClick}
        handleAddAllocation={handleAddAllocation}
      />
    </div>
  );
};

ResourceRequestChart.propTypes = {
  chartDates: PropTypes.array.isRequired,
  resourceRequests: PropTypes.array.isRequired,
  scale: PropTypes.string,
  isEditSwitchEnabled: PropTypes.bool.isRequired,
  chartDisplayDateRange: PropTypes.object.isRequired,
  displayUnit: PropTypes.string,
  handleAddResourceRequestClick: PropTypes.func,
  sort: PropTypes.object,
  onSortChange: PropTypes.func,
  hasMoreRows: PropTypes.bool.isRequired,
  loadMoreRows: PropTypes.func.isRequired,
  loadingMoreRows: PropTypes.bool.isRequired,
  loadingRows: PropTypes.bool
};

export default ResourceRequestChart;
